import React from 'react';
import {
  Brain,
  Check,
  Crown,
  FileText,
  Image as ImageIcon,
  Lock,
  Pin,
  PinOff,
  Search,
  Sparkles,
  Star,
  Video,
  Volume2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import PremiumFeatureAlert from '../ui/alert';
import DropdownButton from './DropdownButton';
import { useDropdownState } from '../../hooks/dropdown/useDropdownState';
import { useModelData, getProviderGroupName } from '../../hooks/dropdown/useModelData';
import { useModelSelection } from '../../hooks/dropdown/useModelSelection';
import { usePinnedModel } from '../../hooks/dropdown/usePinnedModel';

const getSymbolImage = (symbolName, isDarkMode) => {
  const mode = isDarkMode ? 'dark' : 'light';
  return `/symbols/${symbolName}.${mode}.png`;
};

const capabilityMeta = [
  { key: 'images', label: 'Images', Icon: ImageIcon, colorClass: 'text-sky-500' },
  { key: 'files', label: 'Files', Icon: FileText, colorClass: 'text-amber-500' },
  { key: 'audio', label: 'Audio', Icon: Volume2, colorClass: 'text-violet-500' },
  { key: 'video', label: 'Video', Icon: Video, colorClass: 'text-rose-500' },
  { key: 'reasoning', label: 'Reasoning', Icon: Brain, colorClass: 'text-indigo-500' },
];

const providerInitial = (label = '') => {
  if (label === 'Auto Selection') return 'A';
  if (label === 'xAI') return 'x';
  return (label[0] || '?').toUpperCase();
};

const FAVORITE_MODELS_STORAGE_KEY = 'favorite-models';

const readFavoriteModelValues = () => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITE_MODELS_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch {
    return [];
  }
};

const saveFavoriteModelValues = (values) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAVORITE_MODELS_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Ignore storage failures; favorites are a convenience preference.
  }
};

const providerSvgIcons = {
  'Auto Selection': {
    viewBox: '250 160 530 700',
    polygons: [
      {
        points: '745.39 511.52 523.56 511.52 277.91 186.88 745.39 511.52',
        opacity: 1,
      },
      {
        points: '746.09 512 598.1 614.77 523.56 511.52 745.39 511.52 746.09 512',
        opacity: 0.82,
      },
      {
        points: '598.1 614.77 277.91 837.12 448.42 511.52 523.56 511.52 598.1 614.77',
        opacity: 0.64,
      },
    ],
  },
  OpenAI: {
    viewBox: '110 110 500 500',
    path: 'M304.246 295.411V249.828C304.246 245.989 305.687 243.109 309.044 241.191L400.692 188.412C413.167 181.215 428.042 177.858 443.394 177.858C500.971 177.858 537.44 222.482 537.44 269.982C537.44 273.34 537.44 277.179 536.959 281.018L441.954 225.358C436.197 222 430.437 222 424.68 225.358L304.246 295.411ZM518.245 472.945V364.024C518.245 357.304 515.364 352.507 509.608 349.149L389.174 279.096L428.519 256.543C431.877 254.626 434.757 254.626 438.115 256.543L529.762 309.323C556.154 324.679 573.905 357.304 573.905 388.971C573.905 425.436 552.315 459.024 518.245 472.941V472.945ZM275.937 376.982L236.592 353.952C233.235 352.034 231.794 349.154 231.794 345.315V239.756C231.794 188.416 271.139 149.548 324.4 149.548C344.555 149.548 363.264 156.268 379.102 168.262L284.578 222.964C278.822 226.321 275.942 231.119 275.942 237.838V376.986L275.937 376.982ZM360.626 425.922L304.246 394.255V327.083L360.626 295.416L417.002 327.083V394.255L360.626 425.922ZM396.852 571.789C376.698 571.789 357.989 565.07 342.151 553.075L436.674 498.374C442.431 495.017 445.311 490.219 445.311 483.499V344.352L485.138 367.382C488.495 369.299 489.936 372.179 489.936 376.018V481.577C489.936 532.917 450.109 571.785 396.852 571.785V571.789ZM283.134 464.79L191.486 412.01C165.094 396.654 147.343 364.029 147.343 332.362C147.343 295.416 169.415 262.309 203.48 248.393V357.791C203.48 364.51 206.361 369.308 212.117 372.665L332.074 442.237L292.729 464.79C289.372 466.707 286.491 466.707 283.134 464.79ZM277.859 543.48C223.639 543.48 183.813 502.695 183.813 452.314C183.813 448.475 184.294 444.636 184.771 440.797L279.295 495.498C285.051 498.856 290.812 498.856 296.568 495.498L417.002 425.927V471.509C417.002 475.349 415.562 478.229 412.204 480.146L320.557 532.926C308.081 540.122 293.206 543.48 277.854 543.48H277.859ZM396.852 600.576C454.911 600.576 503.37 559.313 514.41 504.612C568.149 490.696 602.696 440.315 602.696 388.976C602.696 355.387 588.303 322.762 562.392 299.25C564.791 289.173 566.231 279.096 566.231 269.024C566.231 200.411 510.571 149.067 446.274 149.067C433.322 149.067 420.846 150.984 408.37 155.305C386.775 134.192 357.026 120.758 324.4 120.758C266.342 120.758 217.883 162.02 206.843 216.721C153.104 230.637 118.557 281.018 118.557 332.357C118.557 365.946 132.95 398.571 158.861 422.083C156.462 432.16 155.022 442.237 155.022 452.309C155.022 520.922 210.682 572.266 274.978 572.266C287.931 572.266 300.407 570.349 312.883 566.028C334.473 587.141 364.222 600.576 396.852 600.576Z',
  },
  Anthropic: {
    viewBox: '0 0 46 32',
    path: 'M32.73 0h-6.945L38.45 32h6.945L32.73 0ZM12.665 0 0 32h7.082l2.59-6.72h13.25l2.59 6.72h7.082L19.929 0h-7.264Zm-.702 19.337 4.334-11.246 4.334 11.246h-8.668Z',
  },
  Google: {
    viewBox: '0 0 16 16',
    path: 'M16 8.016A8.522 8.522 0 008.016 16h-.032A8.521 8.521 0 000 8.016v-.032A8.521 8.521 0 007.984 0h.032A8.522 8.522 0 0016 7.984v.032z',
  },
  Perplexity: {
    viewBox: '0 0 24 24',
    path: 'M19.785 0v7.272H22.5V17.62h-2.935V24l-7.037-6.194v6.145h-1.091v-6.152L4.392 24v-6.465H1.5V7.188h2.884V0l7.053 6.494V.19h1.09v6.49L19.786 0zm-7.257 9.044v7.319l5.946 5.234V14.44l-5.946-5.397zm-1.099-.08l-5.946 5.398v7.235l5.946-5.234V8.965zm8.136 7.58h1.844V8.349H13.46l6.105 5.54v2.655zm-8.982-8.28H2.59v8.195h1.8v-2.576l6.192-5.62zM5.475 2.476v4.71h5.115l-5.115-4.71zm13.219 0l-5.115 4.71h5.115v-4.71z',
  },
  xAI: {
    viewBox: '0 0 33 32',
    path: 'M12.745 20.54l10.97-8.19c.539-.4 1.307-.244 1.564.38 1.349 3.288.746 7.241-1.938 9.955-2.683 2.714-6.417 3.31-9.83 1.954l-3.728 1.745c5.347 3.697 11.84 2.782 15.898-1.324 3.219-3.255 4.216-7.692 3.284-11.693l.008.009c-1.351-5.878.332-8.227 3.782-13.031L33 0l-4.54 4.59v-.014L12.743 20.544m-2.263 1.987c-3.837-3.707-3.175-9.446.1-12.755 2.42-2.449 6.388-3.448 9.852-1.979l3.72-1.737c-.67-.49-1.53-1.017-2.515-1.387-4.455-1.854-9.789-.931-13.41 2.728-3.483 3.523-4.579 8.94-2.697 13.561 1.405 3.454-.899 5.898-3.22 8.364C1.49 30.2.666 31.074 0 32l10.478-9.466',
  },
  DeepSeek: {
    viewBox: '0 0 24 24',
    path: 'M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z',
  },
  Meta: {
    viewBox: '0 0 32 32',
    path: 'M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6  C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1  c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5  c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7  C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11',
  },
};

const ProviderSvgIcon = ({ provider }) => (
  <svg
    viewBox={provider.viewBox}
    className="h-5 w-5"
    fill="currentColor"
    aria-hidden="true"
  >
    {provider.path && <path d={provider.path} />}
    {provider.polygons?.map((polygon, index) => (
      <polygon
        key={index}
        points={polygon.points}
        opacity={polygon.opacity}
      />
    ))}
  </svg>
);

const ProviderLogoIcon = ({ label }) => {
  const provider = providerSvgIcons[label];
  return provider ? <ProviderSvgIcon provider={provider} /> : null;
};

const ModelIcon = ({ model, isDarkMode }) => {
  if (model?.symbol) {
    return (
      <img
        src={getSymbolImage(model.symbol, isDarkMode)}
        alt=""
        className="h-6 w-6 object-contain"
        aria-hidden="true"
      />
    );
  }

  return <Sparkles size={18} aria-hidden="true" />;
};

const CapabilityIcons = ({ capabilities = {}, disabled = false }) => {
  const activeCapabilities = capabilityMeta.filter(({ key }) => capabilities[key]);

  if (activeCapabilities.length === 0) {
    return null;
  }

  const labels = activeCapabilities.map(({ label }) => label).join(', ');

  return (
    <div
      className={`hidden h-8 flex-shrink-0 items-center gap-1 rounded-full border px-2 sm:flex ${
        disabled
          ? 'theme-border bg-[var(--color-button-secondary)] opacity-60 grayscale'
          : 'theme-border bg-[var(--color-button-secondary)]'
      }`}
      aria-label={labels}
      title={labels}
    >
      {activeCapabilities.map(({ key, label, Icon, colorClass }) => (
        <span
          key={key}
          title={label}
          aria-label={label}
          className={`inline-flex h-5 w-5 items-center justify-center ${
            disabled ? 'text-[var(--color-text-muted)]' : colorClass
          }`}
        >
          <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
        </span>
      ))}
    </div>
  );
};

const FavoriteModelButton = ({
  isFavorite,
  isPendingRemoval,
  onClick,
}) => {
  const title = !isFavorite
    ? 'Add to favorites'
    : isPendingRemoval
      ? 'Click again to remove'
      : 'Remove from favorites';

  return (
    <span className="favorite-model-control relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        aria-pressed={isFavorite}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
          isPendingRemoval
            ? 'text-red-500 hover:text-red-500'
            : isFavorite
              ? 'text-[var(--color-warning)] hover:text-[var(--color-warning)]'
              : 'theme-text-muted hover:text-[var(--color-warning)]'
        }`}
      >
        <Star
          size={15}
          fill={isFavorite ? 'currentColor' : 'none'}
          aria-hidden="true"
        />
      </button>
      <span className="favorite-model-tooltip pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-medium opacity-0 shadow-lg transition-opacity bg-[var(--color-message-input-background)] theme-border theme-text-primary">
        {title}
      </span>
    </span>
  );
};

const ModernModelSelector = ({
  value,
  onChange,
  hasSubscription,
  onUpgrade,
  onPinnedModelChange = () => {},
  triggerVariant = 'default',
  containerClassName = 'relative inline-block w-full'
}) => {
  const { theme } = useTheme();
  const isDarkMode =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const {
    isOpen,
    setIsOpen,
    searchTerm,
    setSearchTerm,
    dropdownRef,
    toggleDropdown
  } = useDropdownState();

  const { modelOptions, providers, getSelectedModel } = useModelData(value);
  const {
    showAlert,
    handleOptionClick,
    handleAlertClose
  } = useModelSelection(hasSubscription, onChange);
  const { pinnedModel, handlePinModel, handleUnpinModel } = usePinnedModel(onPinnedModelChange);

  const selectedModel = getSelectedModel();
  const pinnedModelInfo = React.useMemo(
    () => modelOptions.find((m) => m.value === pinnedModel),
    [modelOptions, pinnedModel]
  );
  const [activeProvider, setActiveProvider] = React.useState('');
  const [showLegacy, setShowLegacy] = React.useState(false);
  const [menuHeight, setMenuHeight] = React.useState(420);
  const [favoriteModelValues, setFavoriteModelValues] = React.useState(readFavoriteModelValues);
  const [pendingUnfavoriteValue, setPendingUnfavoriteValue] = React.useState(null);
  const searchInputRef = React.useRef(null);
  const favoriteModelSet = React.useMemo(
    () => new Set(favoriteModelValues),
    [favoriteModelValues]
  );

  React.useEffect(() => {
    if (pinnedModel && modelOptions.length && !pinnedModelInfo) {
      handleUnpinModel();
    }
  }, [pinnedModel, pinnedModelInfo, modelOptions, handleUnpinModel]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setIsOpen]);

  React.useEffect(() => {
    if (isOpen) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuHeight = () => {
      const rect = dropdownRef.current?.getBoundingClientRect();
      if (!rect) return;
      const availableBelow = window.innerHeight - rect.bottom - 16;
      setMenuHeight(Math.max(180, Math.min(520, availableBelow)));
    };

    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);
    window.addEventListener('scroll', updateMenuHeight, true);

    return () => {
      window.removeEventListener('resize', updateMenuHeight);
      window.removeEventListener('scroll', updateMenuHeight, true);
    };
  }, [isOpen, dropdownRef]);

  React.useEffect(() => {
    saveFavoriteModelValues(favoriteModelValues);
  }, [favoriteModelValues]);

  React.useEffect(() => {
    if (pendingUnfavoriteValue && !favoriteModelSet.has(pendingUnfavoriteValue)) {
      setPendingUnfavoriteValue(null);
    }
  }, [favoriteModelSet, pendingUnfavoriteValue]);

  React.useEffect(() => {
    if (!isOpen) {
      setPendingUnfavoriteValue(null);
    }
  }, [isOpen]);

  const filteredModels = React.useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return (modelOptions || []).filter((model) => {
      const providerGroup = getProviderGroupName(model.provider);
      const matchesProvider =
        activeProvider === 'favorites'
          ? favoriteModelSet.has(model.value)
          : !activeProvider || providerGroup === activeProvider;
      const matchesSearch =
        search === '' ||
        model.display.toLowerCase().includes(search) ||
        (model.subtitle || '').toLowerCase().includes(search) ||
        (model.description || '').toLowerCase().includes(search) ||
        providerGroup.toLowerCase().includes(search);
      const matchesLegacy = showLegacy || !model.isOld;
      return matchesProvider && matchesSearch && matchesLegacy;
    });
  }, [activeProvider, favoriteModelSet, modelOptions, searchTerm, showLegacy]);

  const sortedModels = React.useMemo(() => {
    const bySortOrder = (a, b) => {
      const aSort = Number.isFinite(a.sortOrder) ? a.sortOrder : Number.MAX_SAFE_INTEGER;
      const bSort = Number.isFinite(b.sortOrder) ? b.sortOrder : Number.MAX_SAFE_INTEGER;
      if (aSort !== bSort) return aSort - bSort;
      return (a.display || '').localeCompare(b.display || '');
    };
    return [...filteredModels].sort(bySortOrder);
  }, [filteredModels]);

  const favoriteCount = React.useMemo(
    () => modelOptions.filter((model) => favoriteModelSet.has(model.value) && (showLegacy || !model.isOld)).length,
    [favoriteModelSet, modelOptions, showLegacy]
  );

  const legacyCount = React.useMemo(
    () => modelOptions.filter((model) => model.isOld).length,
    [modelOptions]
  );

  const visibleModelCount = React.useMemo(
    () => modelOptions.filter((model) => showLegacy || !model.isOld).length,
    [modelOptions, showLegacy]
  );

  const premiumLockedCount = React.useMemo(
    () => modelOptions.filter((model) => model.isPremium && !hasSubscription).length,
    [hasSubscription, modelOptions]
  );

  const providerFilters = React.useMemo(() => ([
    {
      key: '',
      label: 'All',
      count: visibleModelCount,
      Icon: Sparkles,
    },
    {
      key: 'favorites',
      label: 'Favorites',
      count: favoriteCount,
      Icon: Star,
    },
    ...providers.map((provider) => ({
      ...provider,
      Icon: null,
    })),
  ]), [favoriteCount, providers, visibleModelCount]);

  const handleRowClick = (model, event) => {
    handleOptionClick(model, event, setIsOpen);
  };

  const handleFavoriteToggle = React.useCallback((event, model, isFavoriteModel) => {
    event.preventDefault();
    event.stopPropagation();

    if (!model?.value || model.isAuto) return;

    if (!isFavoriteModel) {
      setFavoriteModelValues((previous) => (
        previous.includes(model.value) ? previous : [...previous, model.value]
      ));
      setPendingUnfavoriteValue(null);
      return;
    }

    if (pendingUnfavoriteValue === model.value) {
      setFavoriteModelValues((previous) => previous.filter((value) => value !== model.value));
      setPendingUnfavoriteValue(null);
      return;
    }

    setPendingUnfavoriteValue(model.value);
  }, [pendingUnfavoriteValue]);

  const handlePinToggle = React.useCallback((event, model, isPinnedModel) => {
    event.stopPropagation();
    if (model.isPremium && !hasSubscription) return;

    if (isPinnedModel) {
      handleUnpinModel();
    } else {
      handlePinModel(model);
    }
  }, [hasSubscription, handlePinModel, handleUnpinModel]);

  const menuPlacementClass = triggerVariant === 'composer'
    ? 'top-full mt-2 origin-top-right'
    : 'top-full mt-2 origin-top-right';
  const pickerBorderColor = isDarkMode
    ? 'color-mix(in srgb, var(--color-message-input-background), white 14%)'
    : 'color-mix(in srgb, var(--color-message-input-background), black 10%)';
  const pickerDividerColor = isDarkMode
    ? 'color-mix(in srgb, var(--color-message-input-background), white 10%)'
    : 'color-mix(in srgb, var(--color-message-input-background), black 8%)';

  return (
    <div className={containerClassName} ref={dropdownRef}>
      <DropdownButton
        selectedModel={selectedModel}
        isOpen={isOpen}
        onClick={toggleDropdown}
        isDarkMode={isDarkMode}
        variant={triggerVariant}
      />

      {isOpen && (
        <div
          className={`absolute right-0 z-[1000] flex flex-col overflow-hidden rounded-2xl border bg-[var(--color-message-input-background)] theme-border theme-text-primary shadow-2xl ${menuPlacementClass}`}
          style={{
            width: 'min(680px, calc(100vw - 2rem))',
            height: `${menuHeight}px`,
            borderColor: pickerBorderColor,
            boxShadow: isDarkMode
              ? '0 24px 70px rgba(0, 0, 0, 0.62)'
              : '0 24px 60px rgba(15, 23, 42, 0.18)'
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {!hasSubscription && premiumLockedCount > 0 && (
            <div
              className="flex items-center justify-between gap-3 border-b px-4 py-3 bg-[var(--color-button-secondary)]"
              style={{ borderBottomColor: pickerDividerColor }}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">Unlock premium models</p>
                <p className="text-xs theme-text-secondary">
                  {premiumLockedCount} premium model{premiumLockedCount === 1 ? '' : 's'} available
                </p>
              </div>
              {onUpgrade && (
                <button
                  type="button"
                  onClick={onUpgrade}
                  className="flex-shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold theme-button-primary"
                >
                  Upgrade
                </button>
              )}
            </div>
          )}

          <div className="grid min-h-0 flex-1 grid-cols-[64px_minmax(0,1fr)]">
            <aside
              className="model-picker-provider-rail overflow-y-auto border-r bg-[var(--color-message-input-background)] py-3"
              style={{ borderRightColor: pickerDividerColor }}
            >
              <div className="flex flex-col items-center gap-1">
                {providerFilters.map((provider, index) => {
                  const isActive = activeProvider === provider.key;
                  const ProviderIcon = provider.Icon;
                  const hasProviderLogo = Boolean(providerSvgIcons[provider.label]);
                  return (
                    <React.Fragment key={provider.key || 'all'}>
                      {index === 2 && (
                        <div
                          className="my-1 h-px w-7"
                          style={{ backgroundColor: pickerDividerColor }}
                          aria-hidden="true"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setPendingUnfavoriteValue(null);
                          setActiveProvider((current) => (
                            current === provider.key ? '' : provider.key
                          ));
                        }}
                        title={provider.label}
                        aria-label={`Filter ${provider.label}`}
                        className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                          isActive
                            ? 'theme-button-primary'
                            : 'theme-text-secondary hover:bg-[var(--color-button-secondary-hover)]'
                        }`}
                      >
                        {ProviderIcon ? (
                          <ProviderIcon size={18} aria-hidden="true" />
                        ) : hasProviderLogo ? (
                          <ProviderLogoIcon label={provider.label} />
                        ) : (
                          <span className="text-sm font-bold" aria-hidden="true">
                            {providerInitial(provider.label)}
                          </span>
                        )}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </aside>

            <section className="flex min-h-0 flex-col">
              <div
                className="border-b px-4 py-3"
                style={{ borderBottomColor: pickerDividerColor }}
              >
                <p className="text-sm font-semibold">
                  {activeProvider === 'favorites'
                    ? 'Favorite Models'
                    : activeProvider || 'All Models'}
                </p>

                <div className="mt-3 flex items-center gap-2">
                  <div
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border px-3 py-2 theme-message-input"
                    style={{ borderColor: pickerBorderColor }}
                  >
                    <Search size={16} className="theme-text-muted" aria-hidden="true" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search models..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      className="w-full bg-transparent text-sm outline-none theme-text-primary placeholder:text-[var(--color-text-muted)]"
                    />
                  </div>
                  {legacyCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowLegacy((prev) => !prev)}
                      className={`flex h-10 flex-shrink-0 items-center gap-1.5 rounded-xl border px-3 text-xs font-medium transition-colors ${
                        showLegacy
                          ? 'theme-button-primary border-transparent'
                          : 'theme-button-secondary theme-border theme-text-secondary hover:bg-[var(--color-button-secondary-hover)]'
                      }`}
                      aria-pressed={showLegacy}
                    >
                      Legacy
                    </button>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                {sortedModels.length === 0 && (
                  <div className="px-4 py-10 text-center text-sm theme-text-muted">
                    No models match your filters.
                  </div>
                )}

                {sortedModels.map((model) => {
                  const isSelected = model.value === value;
                  const isPinned = model.value === pinnedModel;
                  const isLocked = model.isPremium && !hasSubscription;
                  const canPin = !model.isAuto && !isLocked;
                  const isFavorite = favoriteModelSet.has(model.value);
                  const isPendingFavoriteRemoval = pendingUnfavoriteValue === model.value;
                  const subtitle = model.subtitle || model.description || 'General purpose model';

                  return (
                    <div
                      key={model.value}
                      role="button"
                      tabIndex={0}
                      onClick={(event) => handleRowClick(model, event)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRowClick(model, event);
                        }
                      }}
                      className={`group w-full rounded-xl px-3 py-3 text-left transition-colors ${
                        isSelected
                          ? 'bg-[var(--color-model-card-active)] shadow-[inset_0_0_0_1px_var(--color-model-card-active-border)]'
                          : 'hover:bg-[var(--color-model-card-hover)]'
                      } ${isLocked ? 'opacity-45 grayscale' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-button-secondary)]">
                          <ModelIcon model={model} isDarkMode={isDarkMode} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm font-semibold">
                              {model.display}
                            </span>
                            {isSelected && (
                              <Check size={15} className="flex-shrink-0 text-[var(--color-button-primary)]" aria-hidden="true" />
                            )}
                            {!model.isAuto && (
                              <FavoriteModelButton
                                isFavorite={isFavorite}
                                isPendingRemoval={isPendingFavoriteRemoval}
                                onClick={(event) => handleFavoriteToggle(event, model, isFavorite)}
                              />
                            )}
                            {model.badge && (
                              <span className="flex-shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold theme-button-primary">
                                {model.badge}
                              </span>
                            )}
                            {isLocked && (
                              <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold theme-border theme-text-muted">
                                <Lock size={11} aria-hidden="true" />
                                Premium
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-xs theme-text-muted">
                            {subtitle}
                          </p>
                        </div>

                        <CapabilityIcons capabilities={model.capabilities} disabled={isLocked} />

                        {model.isAuto ? (
                          <span className="w-8 text-right text-xs theme-text-muted">
                            Auto
                          </span>
                        ) : canPin ? (
                          <button
                            type="button"
                            onClick={(event) => handlePinToggle(event, model, isPinned)}
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                              isPinned
                                ? 'bg-[var(--color-warning)] text-[var(--color-text-on-accent)] border-[var(--color-warning)]'
                                : 'theme-border theme-button-secondary theme-text-secondary hover:bg-[var(--color-button-secondary-hover)]'
                            }`}
                            aria-pressed={isPinned}
                            aria-label={isPinned ? 'Unpin model as default' : 'Pin model as default'}
                          >
                            {isPinned ? <Pin size={13} /> : <PinOff size={13} />}
                          </button>
                        ) : (
                          <Crown size={17} className="mx-1 flex-shrink-0 theme-text-muted" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      )}

      {showAlert && (
        <PremiumFeatureAlert
          primaryText="Premium Feature"
          subText="This model is only available to premium subscribers. Upgrade now to access advanced AI capabilities."
          onSubscribe={onUpgrade}
          onClose={handleAlertClose}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default ModernModelSelector;
